/**
 * @fileoverview Reset-password card for the /reset-password link emailed by Forgot Password.
 * @description Reads the link's `token` query parameter and verifies it
 * (POST /identity/password/reset/verify) before showing anything to type into, then renders one
 * of four screens inside AuthLayout, with the same logo/card chrome as the other auth screens: a
 * spinner while verifying; the new-password form (ResetPasswordForm) for a usable link; an
 * "invalid or expired link" screen for a missing token or a 1031 (the backend gives every unusable
 * reason that one code), offering a fresh link via /forgot-password; or a "try again" screen for a
 * transient failure (rate limit, service briefly down, network), since the link itself may still
 * be fine.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'

import logoUrl from '@/assets/images/login/logo.png'
import forbiddenIcon from '@/assets/svg/errors/icon-forbidden.svg'
import serverErrorIcon from '@/assets/svg/errors/icon-server-error.svg'
import lockIcon from '@/assets/svg/login/icon-lock.svg'
import ResetPasswordForm from '@/features/auth/components/ResetPasswordForm'
import { useVerifyPasswordReset } from '@/features/auth/hooks/useVerifyPasswordReset'
import Button from '@/shared/components/atoms/Button'
import LinkButton from '@/shared/components/atoms/LinkButton'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { ROUTES } from '@/shared/constants/routes.constants'

import type { ReactNode } from 'react'

interface CardShellProps {
  children: ReactNode
  centered?: boolean
}

/**
 * @description Renders the logo and white card every reset-password screen sits in.
 * @param {CardShellProps} props - The card's content and alignment.
 * @param {ReactNode} props.children - The card's content.
 * @param {boolean} [props.centered] - Center-align the content (the status screens) rather than
 * left-align it (the form).
 */
function CardShell({ children, centered = false }: CardShellProps) {
  return (
    <div className="flex w-full flex-col items-center">
      <img src={logoUrl} alt="InsightBox" className="mb-2 h-18 sm:h-22 lg:h-24 w-auto" />
      <div
        data-testid="reset-password-card"
        className={`mt-8 w-full rounded-[18px] border border-primary/10 bg-white p-6 shadow-card sm:p-8 lg:p-10 ${centered ? 'text-center' : ''}`}
      >
        {children}
      </div>
    </div>
  )
}

interface StatusScreenProps {
  icon: string
  title: string
  description: string
  action: ReactNode
  testId: string
}

/**
 * @description Renders a centered icon/title/description/action status screen.
 * @param {StatusScreenProps} props - The screen's icon, copy and primary action.
 * @param {string} props.icon - The badge icon's URL.
 * @param {string} props.title - The heading.
 * @param {string} props.description - The explanatory text under the heading.
 * @param {ReactNode} props.action - The primary action button.
 * @param {string} props.testId - QA locator prefix: the title, description and back link get
 * "-title", "-description" and "-back-to-sign-in-link" appended.
 */
function StatusScreen({ icon, title, description, action, testId }: StatusScreenProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <CardShell centered>
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-error/10">
        <img src={icon} alt="" className="h-7 w-7" />
      </div>
      <h1
        className="mb-2.5 text-[24px] font-extrabold leading-[1.3] text-ink"
        data-testid={`${testId}-title`}
      >
        {title}
      </h1>
      <p
        className="mx-auto max-w-[320px] text-[14px] leading-[22px] text-slate"
        data-testid={`${testId}-description`}
      >
        {description}
      </p>
      <div className="mt-7 flex flex-col items-center gap-3">
        {action}
        <LinkButton
          size="sm"
          onClick={() => void navigate(ROUTES.LOGIN)}
          data-testid={`${testId}-back-to-sign-in-link`}
        >
          {t('forgotPassword.backToSignIn')}
        </LinkButton>
      </div>
    </CardShell>
  )
}

/**
 * @component
 */
function ResetPasswordCard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const verifyQuery = useVerifyPasswordReset(token)

  if (token.length > 0 && verifyQuery.isPending) {
    return (
      <CardShell>
        <div className="flex justify-center">
          <span
            className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary"
            role="status"
            data-testid="reset-password-loading"
            aria-label={t('resetPassword.loading')}
          />
        </div>
      </CardShell>
    )
  }

  if (token.length > 0 && verifyQuery.isError) {
    if (verifyQuery.error.code !== ERROR_CODES.PASSWORD_RESET_TOKEN_INVALID) {
      return (
        <StatusScreen
          testId="reset-password-unavailable"
          icon={serverErrorIcon}
          title={t('resetPassword.unavailable.title')}
          description={verifyQuery.error.message}
          action={
            <Button
              type="button"
              onClick={() => void verifyQuery.refetch()}
              data-testid="reset-password-unavailable-retry-button"
            >
              {t('resetPassword.unavailable.tryAgain')}
            </Button>
          }
        />
      )
    }
  }

  if (token.length === 0 || verifyQuery.isError) {
    return (
      <StatusScreen
        testId="reset-password-invalid-link"
        icon={forbiddenIcon}
        title={t('resetPassword.invalidLink.title')}
        description={t('resetPassword.invalidLink.description')}
        action={
          <Button
            type="button"
            onClick={() => void navigate(ROUTES.FORGOT_PASSWORD)}
            data-testid="reset-password-invalid-link-request-new-link-button"
          >
            {t('resetPassword.requestNewLink')}
          </Button>
        }
      />
    )
  }

  return (
    <CardShell>
      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/15 bg-linear-to-br from-blue-50 to-blue-100">
        <img src={lockIcon} alt="" className="h-5 w-5" />
      </div>
      <h1
        data-testid="reset-password-title"
        className="mt-5 text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]"
      >
        {t('resetPassword.title')}
      </h1>
      <p className="mt-1.5 text-[15px] text-slate sm:text-base">{t('resetPassword.subtitle')}</p>
      <ResetPasswordForm token={token} />
    </CardShell>
  )
}

export default ResetPasswordCard
